/**
 * The course workspace's addressed views (#1393 M4). Desk is the bare course;
 * the others are `view` parameters on it. Sessions, the plan and Materials are
 * Desk's Calendar order and Resources part, never views; their old addresses
 * resolve through `course-address.ts`.
 */
/** The views that stay addressed by `view` (canonical, so stored links need no mapping). */
export const COURSE_ADDRESS_VIEWS = ["coursework", "grades", "people", "notices", "settings"] as const;
export type CourseWorkspaceView = "desk" | (typeof COURSE_ADDRESS_VIEWS)[number];

/** A view switch carries only the selected session, as context. Record
 * identities belong to their own opening action and must not reopen
 * accidentally in another area. Desk is the bare course and carries none. */
export function courseWorkspaceSectionHref(courseHref: string, view: CourseWorkspaceView, sessionId?: string | null): string {
  if (view === "desk") return courseHref;
  const search = new URLSearchParams({ view });
  if (sessionId) search.set("session", sessionId);
  return `${courseHref}?${search}`;
}

/**
 * One session's own page in the course, in the workspace the reader is
 * already in (#321 M1). It grants no authority: the server rechecks the
 * tenant, the membership and the course relationship on every read. Every
 * surface that links into a session uses this, so none invents its own path.
 */
export function courseSessionHref(courseHref: string, sessionId: string): string {
  return `${courseHref}?${new URLSearchParams({ session: sessionId })}`;
}

/**
 * Desk's Calendar order, optionally with a session row as context or an
 * unplaced plan (`new` opens the new-plan editor).
 */
export function courseCalendarHref(courseHref: string, row: { session?: string | null; plan?: string | null } = {}): string {
  const search = new URLSearchParams({ order: "calendar" });
  if (row.session) search.set("session", row.session);
  if (row.plan) search.set("plan", row.plan);
  return `${courseHref}?${search}`;
}

/** Desk's Resources part in Outline. */
export function courseResourcesHref(courseHref: string): string {
  return `${courseHref}?part=resources`;
}

/** The course's three primary destinations, in tab order (#1393 contract 5). */
export function courseWorkspaceSections(teaching: boolean): Array<{ view: CourseWorkspaceView; label: string }> {
  return [
    { view: "desk", label: "Desk" },
    { view: "coursework", label: "Coursework" },
    { view: "grades", label: teaching ? "Gradebook" : "Grades" },
  ];
}

/** The header destinations beside the band: People, and Manage course for teaching. */
export function courseHeaderDestinations(teaching: boolean): Array<{ view: CourseWorkspaceView; label: string }> {
  return [
    { view: "people", label: "People" },
    ...(teaching ? [{ view: "settings" as const, label: "Manage course" }] : []),
  ];
}
